import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { X, ExternalLink, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { GeoClusterPoint, GeoContratoPoint, Criticidade } from '@/hooks/useGeoIntelData';
import { CRIT_COLORS, CRIT_LABELS } from './mapHelpers';

type SelectedItem =
  | { type: 'cluster'; data: GeoClusterPoint }
  | { type: 'contrato'; data: GeoContratoPoint };

interface MapDetailPanelProps {
  item: SelectedItem;
  onClose: () => void;
}

function SpiIndicator({ value }: { value: number | null }) {
  if (value === null) return <span className="text-muted-foreground text-xs">—</span>;
  const color = value < 0.7 ? 'text-destructive' : value < 0.9 ? 'text-warning' : 'text-success';
  const Icon = value < 0.9 ? TrendingDown : value > 1.05 ? TrendingUp : Minus;
  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <Icon className="w-3 h-3" />
      <span className="font-semibold text-sm tabular-nums">{value.toFixed(3)}</span>
    </div>
  );
}

function KpiRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

const MapDetailPanel: React.FC<MapDetailPanelProps> = ({ item, onClose }) => {
  const navigate = useNavigate();
  const criticidade = item.type === 'cluster' ? item.data.criticidade : (item.data as GeoContratoPoint).criticidade;

  return (
    <div className="absolute top-3 right-3 bottom-3 z-20 w-72 flex flex-col animate-in slide-in-from-right-4 duration-300">
      <div className="bg-card/95 backdrop-blur-xl border border-border/60 rounded-xl shadow-2xl flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-border/40">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Badge
                  variant="outline"
                  className="text-xs px-1.5 h-5 font-semibold"
                  style={{
                    borderColor: CRIT_COLORS[criticidade],
                    color: CRIT_COLORS[criticidade],
                  }}
                >
                  {CRIT_LABELS[criticidade]}
                </Badge>
                <Badge variant="secondary" className="text-xs px-1.5 h-5">
                  {item.type === 'cluster' ? 'Cluster' : 'Contrato'}
                </Badge>
              </div>
              <h3 className="text-sm font-semibold text-foreground leading-snug truncate">
                {item.type === 'cluster' ? item.data.name : (item.data as GeoContratoPoint).nome}
              </h3>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0 rounded-lg" onClick={onClose}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
          {item.type === 'cluster' && <ClusterDetail cluster={item.data} navigate={navigate} />}
          {item.type === 'contrato' && <ContratoDetail contrato={item.data as GeoContratoPoint} navigate={navigate} />}
        </div>
      </div>
    </div>
  );
};

function ClusterDetail({ cluster, navigate }: { cluster: GeoClusterPoint; navigate: any }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <MetricCard label="Contratos" value={cluster.contratos.length} />
        <MetricCard label="Alertas" value={cluster.total_alertas} variant={cluster.total_alertas > 0 ? 'danger' : 'default'} />
      </div>

      <div className="space-y-0 divide-y divide-border/30">
        <KpiRow label="Desvios abertos">
          <span className={`text-xs font-semibold ${cluster.total_desvios > 0 ? 'text-warning' : 'text-success'}`}>
            {cluster.total_desvios}
          </span>
        </KpiRow>
        <KpiRow label="Coordenadas">
          <span className="text-xs font-mono text-muted-foreground">
            {cluster.has_valid_coordinates
              ? `${cluster.coordinates[1].toFixed(3)}, ${cluster.coordinates[0].toFixed(3)}`
              : 'Fallback'}
          </span>
        </KpiRow>
      </div>

      {cluster.contratos.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Contratos vinculados
          </p>
          <div className="space-y-1">
            {cluster.contratos.map(ec => (
              <button
                key={ec.id}
                onClick={() => navigate(`/empreendimentos/contratos/${ec.id}`)}
                className="w-full flex items-center gap-2 p-2 rounded-lg border border-border/40 hover:bg-muted/40 hover:border-border transition-all text-left group"
              >
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CRIT_COLORS[ec.criticidade] }} />
                <span className="text-xs flex-1 truncate">{ec.nome}</span>
                <ExternalLink className="w-3 h-3 text-muted-foreground/50 group-hover:text-primary flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function ContratoDetail({ contrato, navigate }: { contrato: GeoContratoPoint; navigate: any }) {
  const progressPercent = contrato.percentual_fisico !== null ? contrato.percentual_fisico * 100 : null;

  return (
    <>
      {/* SPI/CPI cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-muted/30 rounded-lg p-2.5 text-center">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1">SPI</p>
          <SpiIndicator value={contrato.spi} />
        </div>
        <div className="bg-muted/30 rounded-lg p-2.5 text-center">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1">CPI</p>
          <SpiIndicator value={contrato.cpi} />
        </div>
      </div>

      {/* Progress bar */}
      {progressPercent !== null && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Avanço físico</span>
            <span className="text-xs font-semibold">{progressPercent.toFixed(1)}%</span>
          </div>
          <Progress value={progressPercent} className="h-1.5" />
        </div>
      )}

      <div className="space-y-0 divide-y divide-border/30">
        <KpiRow label="Cluster">
          <span className="text-xs font-medium truncate max-w-[120px]">{contrato.cluster_name}</span>
        </KpiRow>
        <KpiRow label="Status">
          <Badge variant="outline" className="text-xs h-5 px-1.5">{contrato.status}</Badge>
        </KpiRow>
        <KpiRow label="Alertas ativos">
          <span className={`text-xs font-semibold ${contrato.alertas_abertos > 0 ? 'text-destructive' : 'text-success'}`}>
            {contrato.alertas_abertos}
          </span>
        </KpiRow>
        <KpiRow label="Desvios abertos">
          <span className={`text-xs font-semibold ${contrato.desvios_abertos > 0 ? 'text-warning' : 'text-success'}`}>
            {contrato.desvios_abertos}
          </span>
        </KpiRow>
      </div>

      <Button
        size="sm"
        className="w-full h-8 text-xs mt-1"
        onClick={() => navigate(`/empreendimentos/contratos/${contrato.id}`)}
      >
        <ExternalLink className="w-3 h-3 mr-1.5" />
        Abrir Portal do Contrato
      </Button>
    </>
  );
}

function MetricCard({ label, value, variant = 'default' }: { label: string; value: number; variant?: 'default' | 'danger' }) {
  return (
    <div className="bg-muted/30 rounded-lg p-2.5 text-center">
      <p className={`text-lg font-bold tabular-nums ${variant === 'danger' && value > 0 ? 'text-destructive' : 'text-foreground'}`}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
    </div>
  );
}

export default MapDetailPanel;
export type { SelectedItem };
